import dayjs, { OpUnitType, QUnitType } from 'dayjs'
import { range } from 'lodash'

import { DEFAULT_TIME_FORMAT, FORMAT_MM_DD_YYYY } from './display'
import { startOfDate } from './manipulations'

type DateFromFormatOptions = {
  utc?: boolean
  strict?: boolean
}

/**
 * Parses a date string using a provided format.
 * @param date Date string to parse.
 * @param parseFormat Dayjs parse format (defaults to `FORMAT_MM_DD_YYYY`).
 * @param options Parse options (`utc`, `strict`).
 * @returns Parsed `Date`, or `null` when parsing fails.
 */
export const getDateFromFormat = (
  date: string,
  parseFormat: string = FORMAT_MM_DD_YYYY,
  options: DateFromFormatOptions = { utc: false, strict: false },
): Date | null => {
  const d = options.utc
    ? dayjs.utc(date, parseFormat, options.strict)
    : dayjs(date, parseFormat, options.strict)
  return d.isValid() ? d.toDate() : null
}

/**
 * Calculates the difference between two dates.
 * @param date1 Left date (`Date | string`), interpreted in UTC.
 * @param date2 Right date (`Date | string`), interpreted in UTC.
 * @param unit Diff unit (defaults to `day`).
 * @param float Whether to return a float value (defaults to `false`).
 * @returns Numeric difference from `date1 - date2`.
 */
export const getDateDiff = (
  date1: Date | string,
  date2: Date | string,
  unit: QUnitType | OpUnitType = 'day',
  float: boolean = false,
): number => {
  return dayjs.utc(date1).diff(dayjs.utc(date2), unit, float)
}

/**
 * Counts the calendar days between two dates in the local timezone.
 * Unlike `getDateDiff`, it ignores the time of day and DST changes.
 * @param date1 Left date (`Date | string`).
 * @param date2 Right date (`Date | string`).
 * @returns Whole calendar days from `date2` to `date1`.
 */
export const getCalendarDayDiff = (
  date1: Date | string,
  date2: Date | string,
): number => {
  return dayjs(date1).startOf('day').diff(dayjs(date2).startOf('day'), 'day')
}

/**
 * Builds a time range list until end of day.
 * @param startTime Optional start time in `DEFAULT_TIME_FORMAT`.
 * @param minutesInterval Minute step between values (defaults to `15`).
 * @returns Array of formatted time strings.
 */
export const getTimeRange = (
  startTime?: string,
  minutesInterval: number = 15,
): string[] => {
  const start = startTime
    ? dayjs(startTime, DEFAULT_TIME_FORMAT)
    : dayjs().startOf('day')

  const minutesMax = dayjs(start).endOf('day').diff(start, 'minutes')

  return range(0, minutesMax, minutesInterval).map((minuteOffset: number) =>
    dayjs(start).add(minuteOffset, 'minutes').format(DEFAULT_TIME_FORMAT),
  )
}

/**
 * Checks whether two dates are the same for an optional unit.
 * @param date1 First date (`Date | string | null`).
 * @param date2 Second date (`Date | string | null`).
 * @param unit Optional comparison unit, defaults to `milliseconds`.
 * @returns `true` when both dates are valid and equal for the unit.
 */
export const isSameDate = (
  date1: Date | string | null,
  date2: Date | string | null,
  unit?: OpUnitType,
): boolean => {
  const d1 = dayjs.utc(date1)
  const d2 = dayjs.utc(date2)
  return d1.isValid() && d2.isValid() && d1.isSame(d2, unit)
}

/**
 * Checks whether the first date is before the second.
 * @param date1 First date (`Date | string | null`).
 * @param date2 Second date (`Date | string | null`).
 * @param unit Optional comparison unit, defaults to `milliseconds`.
 * @returns `true` when both dates are valid and `date1 < date2`.
 */
export const isDateBefore = (
  date1: Date | string | null,
  date2: Date | string | null,
  unit?: OpUnitType,
): boolean => {
  const d1 = dayjs.utc(date1)
  const d2 = dayjs.utc(date2)
  return d1.isValid() && d2.isValid() && d1.isBefore(d2, unit)
}

/**
 * Checks whether the first date is after the second.
 * @param date1 First date (`Date | string | null`).
 * @param date2 Second date (`Date | string | null`).
 * @param unit Optional comparison unit, defaults to `milliseconds`.
 * @returns `true` when both dates are valid and `date1 > date2`.
 */
export const isDateAfter = (
  date1: Date | string | null,
  date2: Date | string | null,
  unit?: OpUnitType,
): boolean => {
  const d1 = dayjs.utc(date1)
  const d2 = dayjs.utc(date2)
  return d1.isValid() && d2.isValid() && d1.isAfter(d2, unit)
}

/**
 * Checks whether the first date is the same or after the second.
 * @param date1 First date (`Date | string | null`).
 * @param date2 Second date (`Date | string | null`).
 * @param unit Optional comparison unit, defaults to `milliseconds`.
 * @returns `true` when both dates are valid and `date1 >= date2`.
 */
export const isDateSameOrAfter = (
  date1: Date | string | null,
  date2: Date | string | null,
  unit?: OpUnitType,
): boolean => {
  const d1 = dayjs.utc(date1)
  const d2 = dayjs.utc(date2)
  return d1.isValid() && d2.isValid() && d1.isSameOrAfter(d2, unit)
}

/**
 * Checks whether a date is on the current UTC day.
 * @param date Date to compare (`Date | string | null`).
 * @returns `true` when the input is valid and matches today's UTC date.
 */
export const isDateToday = (date: Date | string | null): boolean => {
  if (!date) return false
  const today = dayjs.utc().startOf('day')
  const d = dayjs.utc(date).startOf('day')
  return d.isValid() && d.isSame(today, 'day')
}

/**
 * Returns an english human-readable string with the relative time from now.
 * @param date Date to compare against now (`Date | string | null`).
 * @param withoutSuffix Whether to omit suffix/prefix text (defaults to `true`).
 * @returns Relative time string, or `''` when `date` is invalid.
 */
export const getTimeAgoSince = (
  date: Date | string | null,
  withoutSuffix: boolean = true,
): string => {
  const d = dayjs(date)
  return d.isValid() ? d.fromNow(withoutSuffix) : ''
}

/**
 * Returns a relative day label (e.g. `1d ago`).
 * @param date Date to compare against now (`Date | string | null`).
 * @returns Day label, or `''` when `date` is invalid.
 */
export const getDaysAgo = (date: Date | string | null): string => {
  const d = dayjs(date)
  if (!d.isValid()) {
    return ''
  }

  const days = dayjs().startOf('day').diff(d.startOf('day'), 'day')

  if (days <= 0) {
    return 'Today'
  }

  return `${days}d ago`
}

/**
 * Compares two dates for sorting (descending by default).
 * @param date1 First date (`Date | string | null`).
 * @param date2 Second date (`Date | string | null`).
 * @param unit Comparison unit after normalizing with `startOfDate`.
 * @returns `0` if equal, `-1` if `date1` is after `date2`, else `1`.
 */
export const getDateComparator = (
  date1: Date | string | null,
  date2: Date | string | null,
  unit: OpUnitType = 'day',
): number => {
  const d1 = startOfDate(date1, unit)
  const d2 = startOfDate(date2, unit)
  return isSameDate(d1, d2, unit) ? 0 : isDateAfter(d1, d2, unit) ? -1 : 1
}

/**
 * Guesses the user's IANA timezone identifier.
 * @returns Timezone string (for example, `America/New_York`).
 */
export const guessTimezone = (): string => {
  return dayjs.tz.guess()
}
